import { useEffect, useRef } from 'react'
import { ISeriesApi, UTCTimestamp } from 'lightweight-charts'

/**
 * Подмешивает «живой» close-price в последнюю свечу графика на каждом
 * обновлении bid/ask из RobotStateUpdate (1Hz).  Решает проблему
 * «мороженного» графика на Binance между REST-поллами kline (3 сек).
 *
 * Свеча обновляется только в close — open/high/low оставляем как пришли
 * с биржи.  При смене минуты new candle прилетит через CandlesUpdate,
 * мы поверх него снова начнём подмешивать.
 *
 * НЕ используется на Bybit (там kline идёт WS) и Rithmic (там REST replay
 * + WS bar_update тоже норм).  Но он безопасен — если bid/ask null,
 * ничего не делает.
 *
 * @param series candlestick series
 * @param tickTimeSec время последней свечи (sec, уже с TIME_ZONE_OFFSET)
 * @param bid лучший бид
 * @param ask лучший аск
 * @param lastBarRef ссылка на последнюю свечу (нужно знать o/h/l)
 */
export const useChartLiveTick = (
    series: ISeriesApi<'Candlestick'> | undefined,
    tickTimeSec: number | null | undefined,
    bid: number | null | undefined,
    ask: number | null | undefined,
    lastBar: { open: number; high: number; low: number } | null
) => {
    const lastUpdateTimeRef = useRef<number>(0)

    useEffect(() => {
        if (!series || !tickTimeSec || !lastBar) return
        if (bid == null || ask == null) return
        const mid = (Number(bid) + Number(ask)) / 2
        if (!isFinite(mid)) return
        // v4 strict: update нельзя со временем меньше последнего.  Если
        // robot snapshot отстал от REST-полла WS-bridge (race condition) —
        // пропускаем; следующий снапшот придёт с правильным time.
        if (tickTimeSec < lastUpdateTimeRef.current) return
        try {
            series.update({
                time: tickTimeSec as UTCTimestamp,
                open: lastBar.open,
                high: Math.max(lastBar.high, mid),
                low: Math.min(lastBar.low, mid),
                close: mid,
            })
            lastUpdateTimeRef.current = tickTimeSec
        } catch {
            /* race с REST-update — следующий апдейт разрулит */
        }
    }, [series, tickTimeSec, bid, ask, lastBar])
}
